// src/Dashboard/pages/teacher/Belts/PassageCeintureDashboard.jsx

import React, { useMemo } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import useScrollToTop from "../../../../hooks/useScollToTop";
import { useUser } from "../../../../pages/Home/UserContext";

import { usePassageCeintureDashboard } from "./hooks/usePassageCeintureDashboard";

import BeltsHeader from "./components/BeltsHeader";
import BeltsFiltersCard from "./components/BeltsFiltersCard";
import BeltsKpiCards from "./components/BeltsKpiCards";
import BeltsDataTable from "./components/BeltsDataTable";
import BeltsMobileCards from "./components/BeltsMobileCards";
import BeltsBulkActionBar from "./components/BeltsBulkActionBar";
import BeltsEmptyState from "./components/BeltsEmptyState";
import BeltHistoryDrawer from "./components/BeltHistoryDrawer";

function getStoredUserInfo() {
  try {
    const raw = localStorage.getItem("userInfo");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function getRoleId(user, storedUser) {
  return Number(
    user?.role_id ||
      user?.roleId ||
      storedUser?.role_id ||
      storedUser?.roleId ||
      0
  );
}

function UnauthorizedPanel() {
  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1400px] px-3 sm:px-4 lg:px-6">
        <div className="overflow-hidden rounded-[28px] border border-rose-200 bg-white shadow-sm dark:border-rose-500/20 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-rose-500 to-orange-500" />

          <div className="p-5 sm:p-6">
            <p className="text-[11px] uppercase tracking-[0.22em] text-rose-500">
              Accès refusé
            </p>

            <h1 className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
              Passage de grades
            </h1>

            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Cette page est réservée aux administrateurs et enseignants.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function PassageCeintureDashboard() {
  useScrollToTop();

  const { user } = useUser();
  const storedUser = useMemo(() => getStoredUserInfo(), []);
  const roleId = getRoleId(user, storedUser);
  const authorized = roleId === 1 || roleId === 3;

  const {
    groups,
    adherents,
    belts,
    beltHistories,
    filteredAdherents,
    filters,
    setFilters,
    seasons,
    stats,
    loading,
    selectedIds,
    allChecked,
    toggleAll,
    toggleOne,
    clearSelection,
    bulkBeltId,
    setBulkBeltId,
    bulkDate,
    setBulkDate,
    bulkSaving,
    applyBulk,
    openedAdherent,
    openDrawer,
    closeDrawer,
    refreshHistories,
    refreshAll,
  } = usePassageCeintureDashboard();

  if (!authorized) {
    return <UnauthorizedPanel />;
  }

  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1400px] space-y-5 px-3 sm:px-4 lg:px-6">
        <BeltsHeader onRefresh={refreshAll} loading={loading} />

        <BeltsFiltersCard
          filters={filters}
          setFilters={setFilters}
          groups={groups}
          belts={belts}
          seasons={seasons}
          loading={loading}
          onRefresh={refreshAll}
        />

        <BeltsKpiCards stats={stats} loading={loading} />

        <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

          <div className="p-4 sm:p-5 lg:p-6">
            <div className="flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                  Liste des adhérents
                </p>

                <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
                  Passages de ceintures
                </h2>

                <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                  Sélectionnez plusieurs adhérents pour appliquer un passage de
                  grade par lot, ou ouvrez la chronologie d’un adhérent pour
                  gérer son historique complet.
                </p>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-800/60">
                <span className="text-slate-500 dark:text-slate-400">
                  Résultats :
                </span>{" "}
                <span className="font-bold text-slate-900 dark:text-white">
                  {filteredAdherents.length}
                </span>
                <span className="text-slate-500 dark:text-slate-400">
                  {" "}
                  / {adherents.length}
                </span>
              </div>
            </div>

            {loading ? (
              <div className="mt-5 space-y-3">
                {[...Array(5)].map((_, index) => (
                  <div
                    key={index}
                    className="h-24 animate-pulse rounded-2xl border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-800/60"
                  />
                ))}
              </div>
            ) : filteredAdherents.length === 0 ? (
              <BeltsEmptyState />
            ) : (
              <div className="mt-5">
                <BeltsDataTable
                  adherents={filteredAdherents}
                  beltHistories={beltHistories}
                  selectedIds={selectedIds}
                  allChecked={allChecked}
                  onToggleAll={toggleAll}
                  onToggleOne={toggleOne}
                  onOpenDrawer={openDrawer}
                />

                <BeltsMobileCards
                  adherents={filteredAdherents}
                  beltHistories={beltHistories}
                  selectedIds={selectedIds}
                  onToggleOne={toggleOne}
                  onOpenDrawer={openDrawer}
                />
              </div>
            )}
          </div>
        </section>
      </div>

      <BeltsBulkActionBar
        selectedCount={selectedIds.size}
        belts={belts}
        bulkBeltId={bulkBeltId}
        setBulkBeltId={setBulkBeltId}
        bulkDate={bulkDate}
        setBulkDate={setBulkDate}
        saving={bulkSaving}
        onApply={applyBulk}
        onClear={clearSelection}
      />

      <BeltHistoryDrawer
        isOpen={!!openedAdherent}
        adherent={openedAdherent}
        belts={belts}
        onClose={closeDrawer}
        onSaved={refreshHistories}
      />

      <ToastContainer />
    </section>
  );
}